// PCRDetailsModal.js
import React, { useMemo, useState } from 'react';
import './PCRDetailsModal.css';

const CATS = ['strengths', 'areasOfImprovement', 'missingElements', 'notablePoints'];

const normalize = (s) => (s || '').toLowerCase().trim();
const rankOrder = ['highly recommend', 'recommend', 'consider', 'pass'];
const badgeFor = (rating) => {
  const r = normalize(rating);
  if (r === 'highly recommend') return { cls: 'bg-primary', short: 'HR' };
  if (r === 'recommend')        return { cls: 'bg-success', short: 'R'  };
  if (r === 'consider')         return { cls: 'bg-warning text-dark', short: 'C' };
  if (r === 'pass')             return { cls: 'bg-secondary', short: 'P' };
  return { cls: 'bg-dark', short: '?' };
};

// Map importance -> Bootstrap color
const importanceColor = (level) => {
  const val = normalize(level);
  if (val === 'high') return 'bg-danger';
  if (val === 'medium') return 'bg-warning text-dark';
  if (val === 'low') return 'bg-success';
  return 'bg-secondary';
};

export function PCRDetailsModal({ id, title, pcrList: pcrListRaw = {}, summaries = {}, mostImportant = [] }) {
  // pcrList may arrive as a raw JSON string if the backend early-returned before
  // projecting it (e.g. legacy-only movies). Parse it defensively.
  const pcrList = useMemo(() => {
    if (!pcrListRaw) return {};
    if (typeof pcrListRaw === 'string') {
      try { return JSON.parse(pcrListRaw); } catch { return {}; }
    }
    return pcrListRaw;
  }, [pcrListRaw]);

  const models = useMemo(() => Object.keys(pcrList || {}), [pcrList]);

  const sortedModels = useMemo(() => {
    return [...models].sort((a, b) => {
      const ra = rankOrder.indexOf(normalize(pcrList[a]?.screenplayRating));
      const rb = rankOrder.indexOf(normalize(pcrList[b]?.screenplayRating));
      return (ra === -1 ? 999 : ra) - (rb === -1 ? 999 : rb);
    });
  }, [models, pcrList]);

  const [active, setActive] = useState(sortedModels[0] || null);

  return (
    <div className="modal fade" id={id} tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content border-0 shadow-lg">
          
          {/* Header */}
          <div className="modal-header border-0 text-white" style={{ background: 'linear-gradient(90deg,#083A54,#0d6efd)' }}>
            <h5 className="modal-title">{title || 'Pass / Consider / Recommend'}</h5>
            <button type="button" className="btn-close btn-close-white" data-bs-dismiss="modal" />
          </div>

          <div className="modal-body p-0">
            {/* Most Important Points */}
            {Array.isArray(mostImportant) && mostImportant.length > 0 && (
              <div className="p-3 border-bottom bg-light">
                <h6 className="fw-bold mb-2 text-primary">Most Important Points</h6>
                <ul className="list-group list-group-flush">
                  {mostImportant.map((p, i) => (
                    <li key={i} className="list-group-item small">
                      <div className="fw-semibold">{p.field}</div>
                      <div>{p.content}</div>
                      {p.why_important && <div className="text-muted mt-1">{p.why_important}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tabs */}
            <div className="px-3 pt-3">
              <ul className="nav nav-tabs flex-wrap">
                {sortedModels.map((model) => {
                  const rating = pcrList[model]?.screenplayRating;
                  const { cls, short } = badgeFor(rating);
                  const isActive = active === model;
                  return (
                    <li className="nav-item" key={model}>
                      <button
                        type="button"
                        className={`nav-link d-flex align-items-center gap-2 ${isActive ? 'active fw-bold' : ''}`}
                        onClick={() => setActive(model)}
                      >
                        <span className={`badge ${cls} rounded-pill`}>{short}</span>
                        {model}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            {/* Active Content */}
            <div className="p-3">
              {active ? (
                <ModelPanel model={active} evaluation={pcrList[active]} summaries={summaries} />
              ) : (
                <div className="text-muted">No PCR data.</div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="modal-footer bg-light border-0">
            <button type="button" className="btn btn-outline-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ModelPanel({ model, evaluation = {}, summaries = {} }) {
  const rating = evaluation.screenplayRating ?? '—';
  const { cls, short } = badgeFor(rating);

  return (
    <div className="container-fluid">
      
      {/* Rating + Summaries */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <span className={`badge ${cls} rounded-pill`}>{short}</span>
              <h5 className="mb-0">{model}</h5>
            </div>
            <span className="text-muted small">Screenplay Rating</span>
          </div>
          
          {/* Executive Summary */}
          {(summaries.executive?.content || evaluation.executiveSummary) && (
            <div className="mb-4">
              <h6 className="fw-bold border-bottom pb-1 mb-2 text-primary">
                {summaries.executive?.label || 'For Executives'}
              </h6>
              <div className="p-3 rounded bg-body-tertiary border small" style={{ whiteSpace: 'pre-wrap' }}>
                {summaries.executive?.content || evaluation.executiveSummary || '—'}
              </div>
            </div>
          )}
          
          {/* Writer Summary */}
          {summaries.writer?.content && (
            <div>
              <h6 className="fw-bold border-bottom pb-1 mb-2 text-success">
                {summaries.writer?.label || 'For Writers'}
              </h6>
              <div className="p-3 rounded bg-body-tertiary border small" style={{ whiteSpace: 'pre-wrap' }}>
                {summaries.writer?.content}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Categories */}
      <div className="row g-3">
        {CATS.map((cat) => (
          Array.isArray(evaluation[cat]) && evaluation[cat].length > 0 && (
            <div className="col-md-6" key={cat}>
              <div className="card border-0 shadow-sm h-100">
                <div className="card-header py-2 fw-semibold text-white" style={{ background: 'linear-gradient(90deg,#083A54,#0d6efd)' }}>
                  {pretty(cat)}
                </div>
                <div className="card-body p-2">
                  <ul className="list-group list-group-flush small">
                    {evaluation[cat].map((item, i) => (
                      <li key={i} className="list-group-item">
                        {item.explanation}
                        {item.importance && (
                          <span className={`badge ${importanceColor(item.importance)} rounded-pill ms-2`}>
                            {item.importance}
                          </span>
                        )}
                        {renderSceneRefs(item)}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )
        ))}
      </div>

      {/* Blind Spots */}
      {renderTagList('Blind Spots', evaluation.blind_spots)}

      {/* Amateur Giveaways */}
      {renderTagList('Amateur Giveaways', evaluation.amateur_giveaways)}
    </div>
  );
}

function pretty(s) {
  return s.replace(/([A-Z])/g, ' $1')
          .replace('Of', ' of ')
          .replace(/^./, c => c.toUpperCase());
}

function renderTagList(title, arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  return (
    <div className="card border-0 shadow-sm mt-4">
      <div className="card-header py-2 fw-semibold text-white" style={{ background: 'linear-gradient(90deg,#6c757d,#495057)' }}>
        {title}
      </div>
      <div className="card-body p-2">
        <ul className="list-group list-group-flush small">
          {arr.map((it, i) => (
            <li key={i} className="list-group-item">
              {it.type && <strong className="me-2">{it.type}</strong>}
              {it.description}
              {it.importance && (
                <span className={`badge ${importanceColor(it.importance)} rounded-pill ms-2`}>
                  {it.importance}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function renderSceneRefs(item = {}) {
  const packs = [item.scenes, item.scene_numbers].filter(Boolean);
  const links = [];
  packs.forEach(list => {
    (list || []).forEach((scene) => {
      if (typeof scene === 'string' || typeof scene === 'number') {
        links.push({ key: String(scene), label: `Scene ${scene}` });
      } else if (scene && typeof scene === 'object') {
        const seq = scene.sequence ?? scene.seq ?? scene.id;
        const sc  = scene.scene;
        links.push({ key: `${seq}`, label: `Scene ${seq}${sc ? ` (${sc})` : ''}` });
      }
    });
  });
  if (links.length === 0) return null;
  return (
    <span className="ms-1 text-muted">
      (
      {links.map((lnk, i) => (
        <a key={lnk.key + '-' + i} href="#" className="ms-1 me-1 link-secondary" onClick={(e) => e.preventDefault()}>
          {lnk.label}
        </a>
      ))}
      )
    </span>
  );
}
